import _ from 'lodash';
import reporter from '../privates/reporter';
import * as postMessage from '../privates/postMessage';
import platformUtils from '../privates/platformUtils';
import * as urlUtils from '../privates/urlUtils';
import {validate} from '../privates/validations/validation';

import BaseWindow from '../namespaces/window';
import * as members from '../privates/privateMembers';
import {generateError} from '../privates/validations/errors';

const POPULAR_DESKTOP_HEIGHT = 1067;
const POPULAR_TABLET_HEIGHT = 1024;
const POPULAR_MOBILE_HEIGHT = 900;
const SCREEN_HEIGHT = {
    'Desktop': POPULAR_DESKTOP_HEIGHT,
    'Tablet': POPULAR_TABLET_HEIGHT,
    'Mobile': POPULAR_MOBILE_HEIGHT
};

class Window extends BaseWindow {
    constructor(appDefId) {
        super(appDefId);
        _.bindAll(this, ['openPopup', 'openPersistentPopup', 'getComponentViewportState']);
    }

    openPopup(url, options, compId) {
        return new Promise((resolve, reject) => {
            const appDefId = members._appDefId.get(this);
            const isValid = validate({
                compType: 'popup',
                functionName: 'openPopup',
                dataToValidate: {url, options},
                shouldCheckMandatoryFields: true
            });
            if (!isValid) {
                return reject('Popup arguments are not defined correctly');
            }

            if (!urlUtils.isValidProtocol(url, platformUtils.getRGI())) {
                const errorMessage = `Mix protocol is not allowed. Popup url protocol is http, and the page is https`;
                reporter.reportSdkError(errorMessage);
                return reject(errorMessage);
            }

            const {width, height, position, theme} = options;
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.OPEN_POPUP,
                data: {
                    url,
                    width,
                    height,
                    position,
                    theme,
                    compId,
                    appDefId,
                    requiresDom: {compId},
                    params: {requireDom: compId}
                },
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    openPersistentPopup(url, options, compId) {
        return new Promise((resolve, reject) => {
            const appDefId = members._appDefId.get(this);
            const isValid = validate({
                compType: 'popup',
                functionName: 'openPersistentPopup',
                dataToValidate: {url, options},
                shouldCheckMandatoryFields: true
            });

            if (!isValid) {
                return reject('Popup arguments are not defined correctly');
            }

            if (!urlUtils.isValidProtocol(url, platformUtils.getRGI())) {
                const errorMessage = `Mix protocol is not allowed. Popup url protocol is http, and the page is https`;
                reporter.reportSdkError(errorMessage);
                return reject(errorMessage);
            }

            const {width, height, position, theme} = options;
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.OPEN_PERSISTENT_POPUP,
                data: {
                    url,
                    width,
                    height,
                    position,
                    theme,
                    compId,
                    appDefId,
                    requiresDom: {compId},
                    params: {requireDom: compId}
                },
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    getComponentViewportState(compId, factor = 1.5) {
        const comp = platformUtils.getRMI().getComp(compId);
        if (!comp) {
            const errorMessage = generateError.error_item_not_found({
                propertyName: 'compId',
                wrongValue: compId,
                functionName: 'getComponentViewportState'
            });
            reporter.reportSdkError(errorMessage);
            return Promise.reject(errorMessage);
        }
        const appDefId = members._appDefId.get(this);
        const {applicationId} = platformUtils.getRGI().getAppData(appDefId);
        if (_.get(comp, 'data.applicationId') !== applicationId.toString()) {
            const errorMessage = generateError.error_compId_does_not_exist_for_app({
                compId,
                functionName: 'getComponentViewportState',
                appDefId
            });
            reporter.reportSdkError(errorMessage);
            return Promise.reject(errorMessage);
        }

        if (this.rendering.env === 'backend') {
            const aboveFoldScreenHeight = SCREEN_HEIGHT[this.formFactor] * factor;
            return Promise.resolve({in: _.get(comp, 'layout.y', 0) < aboveFoldScreenHeight});
        }

        return new Promise((resolve, reject) => {
                const callback = (data, error) => { return error ? reject(error) : resolve(data); };
                postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.GET_COMPONENT_VIEWPORT_STATE,
                appDefinitionId: members._appDefId.get(this),
                data: {
                    compId,
                    requiresDom: {compId},
                    params: {requireDom: compId}
                },
                callback
            });
        });
    }

    get isMobileFriendly() {
        return platformUtils.getRGI().getIsMobileFriendly();
    }
}

export default Window;
